import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { OFFER, PUBLIC_STATS_DOC } from '../config/billing';

// L5 — one hook, so the landing pricing card, PlanPage and PaywallModal all
// answer "is the founder price still on, and how many seats are left?" the same
// way. Before this there were four hardcoded copy strings and nothing that knew
// the subscriber count at all.
//
// THE COUNTER counts every paying user, monthly included — not the coupon's
// `times_redeemed`, which counts only yearly founder redemptions. That is a
// deliberate difference with a consequence worth knowing: the public number
// reaches 100 BEFORE the coupon's `max_redemptions` does. The moment it does,
// the promotion code must be archived in Stripe by hand, or the page says the
// seats are gone while the code still works. That is the only manual step in
// this whole mechanism, and it is written down in launch/FIX_LIST.md L5.
//
// It is monotonic on purpose. A cancelled subscriber does not free a seat: the
// promise was to the first hundred people who paid, and one of them having left
// does not make someone later one of the first hundred.

// Session cache. The landing page is the highest-traffic surface in the app and
// this is a Firestore read on every view of it; once per page load is plenty
// for a number that moves a few times a week.
let cached = null;

const readTaken = async () => {
    if (cached !== null) return cached;
    const [collectionId, docId] = PUBLIC_STATS_DOC.split('/');
    const snap = await getDoc(doc(db, collectionId, docId));
    const field = OFFER.counter?.source || 'paidUsersEver';
    const value = snap.exists() ? snap.get(field) : null;
    cached = typeof value === 'number' ? value : null;
    return cached;
};

/**
 * Returns:
 *   active       — render founder copy and prices, or the standard ones
 *   taken        — seats used, or null when unknown (and unknown is not zero)
 *   remaining    — seats left, or null
 *   showCounter  — whether to print the live number at all
 *   copyKey      — which locale copy set to render ('founder' | 'standard')
 *   limit        — the cap, for copy that names it
 */
export const useOffer = () => {
    const [taken, setTaken] = useState(cached);

    useEffect(() => {
        // `counter: null` means the offer runs without a count — no read, no
        // cost, no doc required.
        if (!OFFER.active || !OFFER.counter) return;
        let alive = true;
        readTaken()
            .then((value) => { if (alive) setTaken(value); })
            // A counter we cannot read must NOT close the offer. The €49 stays
            // live; the page simply says "first 100" without a live number.
            // Failing the other way would turn a Firestore hiccup into a price
            // rise for whoever was mid-purchase.
            .catch(() => { if (alive) setTaken(null); });
        return () => { alive = false; };
    }, []);

    const limit = OFFER.limit;
    const soldOut = OFFER.limit != null && taken != null && taken >= OFFER.limit;
    const active = Boolean(OFFER.active && OFFER.code) && !soldOut;

    const counted = taken == null ? null : taken + (OFFER.counter?.baseline || 0);
    const showCounter = Boolean(
        active
        && OFFER.counter
        && counted != null
        // Below the threshold the number is worse than no number: "3 of 100
        // taken" says nobody is buying this.
        && counted >= (OFFER.counter.showFrom ?? 0)
    );

    return {
        active,
        taken: counted,
        remaining: (limit != null && counted != null) ? Math.max(0, limit - counted) : null,
        showCounter,
        copyKey: active ? (OFFER.copyKey || 'founder') : 'standard',
        limit,
    };
};
